// src/redux/slices/broadcastAnalyticsSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { unauthorizedError } from "../../utils/helpers";
import { setSessionExpired, setTotalPage } from "./globalSlice";
import BroadcastAnalyticsService from "../../services/api/BroadcastAnalyticsService";

const Broadcasts = new BroadcastAnalyticsService();

export const getAllBroadcasts = createAsyncThunk(
  "broadcasts/getAll",
  async (
    {
      page,
      limit,
      navigate,
      organisationId,
      searchQuery,
      sortKey,
      sortDirection,
    },
    { dispatch }
  ) => {
    try {
      const response = await Broadcasts.getAllBroadcasts({
        page,
        limit,
      });
      if (response) {
        dispatch(setTotalPage(response?.total));
        return response.broadcasts;
      }
      throw new Error("Failed to fetch broadcasts");
    } catch (error) {
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(error.data?.error || "Failed to fetch broadcasts");
      }
      throw error;
    }
  }
);

export const getBroadcastsByOrgId = createAsyncThunk(
  "broadcasts/getByOrgId",
  async ({ page, limit, id, navigate }, { dispatch }) => {
    try {
      const response = await Broadcasts.getBroadcastsByOrgId({
        page,
        limit,
        id,
      });
      if (response) {
        dispatch(setTotalPage(response?.total));
        return response.broadcasts;
      }
      throw new Error("Failed to fetch broadcasts by organization");
    } catch (error) {
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error.data?.error || "Failed to fetch broadcasts by organization"
        );
      }
      throw error;
    }
  }
);

export const getBroadcastInfo = createAsyncThunk(
  "broadcasts/getBroadcastInfo",
  async ({ id, navigate }, { dispatch }) => {
    try {
      const response = await Broadcasts.getBroadcastInfo(id);
      return response;
    } catch (error) {
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      toast.error(error);
      throw error;
    }
  }
);
export const exportSentUsers = createAsyncThunk(
  "broadcasts/export-success-users",
  async ({ templateName, broadcastedOn, sentUsers }, { dispatch }) => {
    try {
      const response = await Broadcasts.exportSentUsers({
        templateName,
        broadcastedOn,
        userIds:sentUsers,
      });

      toast.success(
        response?.message ||
          "Export Request Sent! You'll receive the file by email."
      );
      return response;
    } catch (error) {
      toast.error(error?.data?.error || "Failed to export users");
      throw error;
    }
  }
);

export const exportFailedUsers = createAsyncThunk(
  "broadcasts/export-failed-users",
  async ({ templateName, broadcastedOn, failedUsers }, { dispatch }) => {
    try {
      const response = await Broadcasts.exportFailedUsers({
        templateName,
        broadcastedOn,
        failedUsers,
      });

      toast.success(
        response?.message ||
          "Export Request Sent! You'll receive the file by email."
      );
      return response;
    } catch (error) {
      toast.error(error?.data?.error || "Failed to export users");
      throw error;
    }
  }
);
const initialState = {
  loading: false,
  broadcasts: null,
  error: null,
  page: 1,
  limit: 10,
  searchQuery: "",
  sortKey: "createdAt",
  sortDirection: "desc",
  organisationId: null,
  broadcastAnalytics: null,
};

const broadcastAnalyticsSlice = createSlice({
  name: "broadcastAnalytics",
  initialState,
  reducers: {
    setBroadcastPage: (state, action) => {
      state.page = action.payload;
    },
    setBroadcastLimit: (state, action) => {
      state.limit = action.payload;
    },
    setBroadcastSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setBroadcastSortKey: (state, action) => {
      state.sortKey = action.payload;
    },
    setBroadcastSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    clearBroadcastInfo: (state) => {
      state.broadcastAnalytics = null;
      state.failed = [];
    },
    resetBroadcastsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get All Broadcasts reducers
      .addCase(getAllBroadcasts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllBroadcasts.fulfilled, (state, action) => {
        state.loading = false;
        state.broadcasts = action.payload;
      })
      .addCase(getAllBroadcasts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get Broadcasts by Org ID reducers
      .addCase(getBroadcastsByOrgId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBroadcastsByOrgId.fulfilled, (state, action) => {
        state.loading = false;
        state.broadcasts = action.payload;
      })
      .addCase(getBroadcastsByOrgId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get Broadcast Info reducers
      .addCase(getBroadcastInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBroadcastInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.broadcastAnalytics = {
          ...action.payload.broadcastAnalytics.analytics,
          templateName: action.payload.broadcastAnalytics.templateName,
          broadcastedOn: action.payload.broadcastAnalytics.broadcastedOn,
        };
      })
      .addCase(getBroadcastInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setBroadcastPage,
  setBroadcastLimit,
  setBroadcastSearchQuery,
  setBroadcastSortKey,
  setBroadcastSortDirection,
  resetBroadcastsState,
  clearBroadcastInfo,
} = broadcastAnalyticsSlice.actions;

export default broadcastAnalyticsSlice.reducer;
